// src/components/common/Toast.tsx
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleExclamation, faCircleInfo, faTriangleExclamation } from '@/icons';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'info' | 'warning';

export interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  duration: number | null; // null = persistente até clique
}

const VARIANT_ICONS: Record<ToastVariant, IconDefinition> = {
  success: faCircleCheck,
  error: faCircleExclamation,
  info: faCircleInfo,
  warning: faTriangleExclamation,
};

interface ToastCardProps {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}

const ToastCard: React.FC<ToastCardProps> = ({ toast, onDismiss }) => {
  const [paused, setPaused] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (toast.duration === null || paused) return;
    timeoutRef.current = window.setTimeout(() => {
      onDismiss(toast.id);
    }, toast.duration);
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [toast.duration, toast.id, paused, onDismiss]);

  return (
    <div
      className="toast-card"
      data-variant={toast.variant}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => onDismiss(toast.id)}
      role="status"
    >
      <FontAwesomeIcon icon={VARIANT_ICONS[toast.variant]} className="toast-icon" />
      <span className="toast-message">{toast.message}</span>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastItem[];
  theme: 'dark' | 'light';
  onDismiss: (id: number) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, theme, onDismiss }) => {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-container" data-theme={theme}>
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default ToastContainer;
